import type { UseFormRegisterReturn } from 'react-hook-form'

/**
 * One `−  n  +` count cell (M23-T12), as the Beds frame `1194:9888` draws it.
 *
 * The typed input stays and the steppers sit either side of it, rather than
 * being replaced by them: five wards × two counts is fifty taps to go from 0 to
 * 25, and an admin correcting a whole ward after a ward round types the number.
 *
 * The buttons **disable at their bounds** so an invalid state cannot be reached
 * by clicking. That is not a substitute for the schema — `free > total` is
 * still refused on submit, and the server still refuses it after that — it just
 * means the obvious interaction cannot produce an error.
 */
export function BedCountCell({
  id,
  label,
  value,
  min = 0,
  max,
  onStep,
  registration,
  error,
}: {
  id: string
  /** Exactly "Total" or "Free" — the column this cell sits under. */
  label: string
  value: number
  min?: number
  max?: number | undefined
  onStep: (next: number) => void
  registration: UseFormRegisterReturn
  error?: string | undefined
}) {
  const atMin = value <= min
  const atMax = max !== undefined && value >= max
  const btn =
    'grid h-7 w-7 shrink-0 place-items-center rounded-[6px] border border-line-2 bg-bg-app text-ink-1 disabled:cursor-not-allowed disabled:opacity-40 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={btn}
          disabled={atMin}
          aria-label={`Decrease ${label.toLowerCase()}`}
          onClick={() => onStep(value - 1)}
        >
          −
        </button>
        {/* The visible column header carries the meaning; this keeps the
            accessible name on the control itself. */}
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
        <input
          id={id}
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className="h-9 w-16 rounded-[8px] border border-line-2 bg-bg-app px-2 text-center text-sm tabular-nums text-ink-1"
          {...registration}
        />
        <button
          type="button"
          className={btn}
          disabled={atMax}
          aria-label={`Increase ${label.toLowerCase()}`}
          onClick={() => onStep(value + 1)}
        >
          +
        </button>
      </div>
      {error && (
        <p id={`${id}-error`} className="text-xs font-medium text-bed-bad">
          {error}
        </p>
      )}
    </div>
  )
}
